import * as React from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactElement;
  side?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
  /** Delay (ms) before showing on hover/focus. Default 250. */
  openDelay?: number;
  /** Disable the tooltip entirely (e.g. when content is empty). */
  disabled?: boolean;
}

interface ChildLikeProps {
  ref?: React.Ref<HTMLElement>;
  onMouseEnter?: React.MouseEventHandler<HTMLElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLElement>;
  onFocus?: React.FocusEventHandler<HTMLElement>;
  onBlur?: React.FocusEventHandler<HTMLElement>;
  onPointerDown?: React.PointerEventHandler<HTMLElement>;
}

/** Hover/focus tooltip that portals to document.body so it isn't clipped
 *  by overflow-hidden parents. Positions itself with viewport collision
 *  detection on the chosen side. */
export function Tooltip({
  content, children, side = 'top', className, openDelay = 250, disabled = false,
}: TooltipProps) {
  const [open, setOpen] = React.useState(false);
  const [coords, setCoords] = React.useState<{ top: number; left: number } | null>(null);
  const triggerRef = React.useRef<HTMLElement | null>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const timerRef = React.useRef<number | null>(null);
  // Тач (планшет): эмулированный mouseenter/focus без mouseleave держал подсказку
  // «Мельче»/«Данные» до следующего тапа (Алексей 20.10 #1631) — от тача не открываемся.
  const touchRef = React.useRef(false);

  const cancelTimer = () => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const show = () => {
    cancelTimer();
    timerRef.current = window.setTimeout(() => setOpen(true), openDelay);
  };
  const hide = () => {
    cancelTimer();
    setOpen(false);
    // Drop the last position: `coords` survives close, so the next open used
    // to PAINT one frame at the previous tooltip position (possibly across
    // the page) before repositioning — the "spawns elsewhere, then flies to
    // the cursor" glitch. Starting from null keeps it hidden until measured.
    setCoords(null);
  };

  // Position once content is rendered.
  React.useLayoutEffect(() => {
    if (!open || !triggerRef.current || !contentRef.current) return;
    // The trigger itself can be mid-flight when the tooltip opens (board tiles
    // FLIP-animate on re-sort), so a rect sampled at open time can sit far from
    // where the trigger lands. Hold the tooltip hidden until the trigger's rect
    // is still across two consecutive frames, then position and reveal.
    let lastRect: DOMRect | null = null;
    let settled = false;
    const compute = () => {
      const el = triggerRef.current;
      // Подсказка «зависала» (Алексей 22.10, #1886: «выйти из полноэкранного режима»):
      // триггер уезжал/становился pointer-events:none под курсором (панель прячется) —
      // mouseleave браузер не шлёт. Если триггер больше не под курсором и не в фокусе
      // с клавиатуры — закрываемся сами.
      if (!el || !el.isConnected || (!el.matches(':hover') && !el.matches(':focus-visible'))) { hide(); return; }
      const tr = el.getBoundingClientRect();
      if (!settled) {
        const still = lastRect != null
          && Math.abs(lastRect.top - tr.top) < 0.5
          && Math.abs(lastRect.left - tr.left) < 0.5;
        lastRect = tr;
        if (!still) return; // keep hidden; the rAF follow re-checks next frame
        settled = true;
      }
      const ct = contentRef.current!.getBoundingClientRect();
      const vpW = window.innerWidth;
      const vpH = window.innerHeight;
      const PAD = 8;
      const GAP = 6;
      let top = 0, left = 0;
      switch (side) {
        case 'top':
          top = tr.top - ct.height - GAP;
          left = tr.left + (tr.width - ct.width) / 2;
          if (top < PAD) top = tr.bottom + GAP;
          break;
        case 'bottom':
          top = tr.bottom + GAP;
          left = tr.left + (tr.width - ct.width) / 2;
          if (top + ct.height > vpH - PAD) top = tr.top - ct.height - GAP;
          break;
        case 'left':
          top = tr.top + (tr.height - ct.height) / 2;
          left = tr.left - ct.width - GAP;
          if (left < PAD) left = tr.right + GAP;
          break;
        case 'right':
          top = tr.top + (tr.height - ct.height) / 2;
          left = tr.right + GAP;
          if (left + ct.width > vpW - PAD) left = tr.left - ct.width - GAP;
          break;
      }
      // Clamp to viewport.
      left = Math.max(PAD, Math.min(left, vpW - ct.width - PAD));
      top = Math.max(PAD, Math.min(top, vpH - ct.height - PAD));
      // Масштаб дашборда (#1880 п.1) больше не на <html>, а на блоке дашборда (#1897): портал в
      // body не увеличен, экранные координаты триггера подходят как есть.
      // Keep the same object when nothing moved — compute runs per frame
      // while open (see the rAF follow below) and must not re-render idly.
      setCoords(prev =>
        prev && Math.abs(prev.top - top) < 0.5 && Math.abs(prev.left - left) < 0.5
          ? prev
          : { top, left },
      );
    };
    compute();
    // Follow the trigger while open: dashboard tiles (and board panels) move
    // via CSS transitions, so a position computed once at open can be a
    // mid-flight snapshot. A per-frame follow keeps the tooltip glued to the
    // trigger; it's one rAF for a single open tooltip — negligible.
    let raf = 0;
    const follow = () => { compute(); raf = window.requestAnimationFrame(follow); };
    raf = window.requestAnimationFrame(follow);
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, true);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute, true);
    };
  }, [open, side]);

  React.useEffect(() => () => cancelTimer(), []);

  if (disabled || content == null || content === '') {
    return children;
  }

  // Clone the child to attach ref + listeners without a wrapper element
  // (avoids breaking the host's flex/grid layout).
  const childProps: ChildLikeProps = {
    ref: (node: HTMLElement | null) => {
      triggerRef.current = node;
      const childRef = (children as React.ReactElement & { ref?: React.Ref<HTMLElement> }).ref;
      if (typeof childRef === 'function') childRef(node);
      else if (childRef && typeof childRef === 'object') {
        (childRef as React.MutableRefObject<HTMLElement | null>).current = node;
      }
    },
    onPointerDown: (e) => {
      touchRef.current = e.pointerType === 'touch' || e.pointerType === 'pen';
      if (touchRef.current) hide();
      (children.props as ChildLikeProps).onPointerDown?.(e);
    },
    onMouseEnter: (e) => {
      if (!touchRef.current) show();
      (children.props as ChildLikeProps).onMouseEnter?.(e);
    },
    onMouseLeave: (e) => {
      hide();
      (children.props as ChildLikeProps).onMouseLeave?.(e);
    },
    onFocus: (e) => {
      // Только фокус с клавиатуры: после клика мышью кнопка остаётся в фокусе, и
      // подсказка открывалась/висела без наведения (#1886).
      if (!touchRef.current && (e.currentTarget as HTMLElement).matches(':focus-visible')) show();
      (children.props as ChildLikeProps).onFocus?.(e);
    },
    onBlur: (e) => {
      hide();
      (children.props as ChildLikeProps).onBlur?.(e);
    },
  };

  return (
    <>
      {React.cloneElement(children, childProps)}
      {open && createPortal(
        <div
          ref={contentRef}
          role="tooltip"
          // Until positioned, render with visibility:hidden — still measurable
          // (so we can size + place it) but it NEVER paints, so it can't flash
          // at 0,0 or a stale spot before snapping to the trigger (Alexey
          // 2026-07-21). Once positioned it APPEARS at the anchor.
          //
          // transition is pinned to OPACITY ONLY — never top/left. The old
          // `animate-in`/inherited transition let the position animate, so the
          // tooltip flew slowly from the 0,0 corner across the screen to the
          // anchor (Alexey 850). Now the position JUMPS to the measured anchor
          // on the first visible paint; only the opacity fades in.
          style={{
            position: 'fixed',
            top: coords?.top ?? 0,
            left: coords?.left ?? 0,
            visibility: coords ? 'visible' : 'hidden',
            opacity: coords ? 1 : 0,
            transition: 'opacity 120ms ease-out',
          }}
          className={cn(
            'pointer-events-none z-[200] max-w-xs whitespace-normal rounded-md bg-foreground px-2 py-1 text-xs leading-snug text-background shadow-md',
            className
          )}
        >
          {content}
        </div>,
        document.body
      )}
    </>
  );
}
